import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isTransparent = transparent && !scrolled;
  const navBg = isTransparent
    ? 'bg-transparent'
    : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70';
  const textColor = isTransparent ? 'text-white' : 'text-foreground-900';
  const dropdownTextColor = 'text-foreground-800';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      {/* Subtle top gradient for transparent mode to ensure text readability */}
      {isTransparent && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-transparent pointer-events-none"></div>
      )}

      <nav className="relative w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" className="flex flex-col leading-none shrink-0">
            <span className={`text-[10px] tracking-[0.25em] font-medium uppercase ${textColor} opacity-80`}>
              Vigor
            </span>
            <span className={`text-lg md:text-xl font-bold tracking-[0.15em] uppercase font-heading ${textColor}`}>
              Buy
            </span>
          </Link>

          {/* Center Nav Links + Dropdown (Desktop) */}
          <div className="hidden md:flex items-center gap-2" ref={dropdownRef}>
            {/* Home */}
            <Link
              to="/"
              className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? 'text-white hover:bg-white/15'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              Home
            </Link>

            {/* Category Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  isTransparent
                    ? 'bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm'
                    : 'bg-background-100 hover:bg-background-200 text-foreground-800'
                }`}
              >
                <i className="ri-menu-line text-base"></i>
                <span>All Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-2xl shadow-lg border border-background-200/80 py-3 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 text-sm ${dropdownTextColor} hover:bg-background-100 transition-colors duration-150 whitespace-nowrap group`}
                    >
                      <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-foreground-800">{cat.name}</span>
                      </div>
                      <span className="text-xs text-foreground-400 shrink-0">{cat.productCount}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide */}
            <Link
              to="/articles/vigorbuy-spreadsheet-complete-guide"
              className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? 'text-white hover:bg-white/15'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              Shopping Guide
            </Link>
          </div>

          {/* Right: CTA Buttons (Desktop) */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? 'bg-white/90 text-foreground-900 hover:bg-white'
                  : 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
              }`}
            >
              Vigorbuy Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              isTransparent
                ? 'text-white bg-white/15 hover:bg-white/25'
                : 'text-foreground-800 bg-background-100 hover:bg-background-200'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 rounded-2xl border border-background-200/80 mt-2 p-4 shadow-lg max-h-[70vh] overflow-y-auto">
            {/* Mobile Nav Links */}
            <div className="flex items-center gap-2 mb-4">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 py-2.5 rounded-full text-sm font-medium text-center bg-background-100 text-foreground-800 hover:bg-background-200 transition-colors"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-home-4-line text-sm"></i>
                </span>
                Home
              </Link>
              <Link
                to="/articles/vigorbuy-spreadsheet-complete-guide"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 py-2.5 rounded-full text-sm font-medium text-center bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-book-open-line text-sm"></i>
                </span>
                Guide
              </Link>
            </div>

            {/* Mobile Categories */}
            <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400 mb-3 px-1">
              Browse Categories
            </p>
            <div className="space-y-1 mb-4">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-xl transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="flex-1">{cat.name}</span>
                  <span className="text-xs text-foreground-400">{cat.productCount}</span>
                </a>
              ))}
            </div>

            {/* Mobile CTAs */}
            <div className="flex flex-col gap-2 pt-3 border-t border-background-200/70">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-3 rounded-full text-sm font-semibold text-center bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Vigorbuy Sheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-3 rounded-full text-sm font-semibold text-center bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}